<!--
Copyright by:

by ESS-Ehrhardt Software Solutions & Service GmbH
Basselscheider Str. 55
D-56281 Emmelshausen
+49 (0) 67 42 / 87 27 0
info@epg.com
-->
<div fxLayout="column" fxLayoutGap="24px" class="container">
  <div fxLayout="row" fxLayoutGap="8px">
    <div fxLayout="column" fxLayoutGap="4px" class="header-area">
      <span class="section_header" i18n="@@emailNotificationForTaskProtocols"
        >Email Notification For Task Protocols</span
      >
      <span i18n="@@sendEmailProtocolDescription" class="description-text"
        >Automatically send email to employees or groups when tasks are completed or incidents
        occur.</span
      >
    </div>
    <div class="delete-button">
      <button
        class="deleteActionButton"
        mat-icon-button
        (click)="onDelete()"
        [disabled]="isDeleting"
      >
        <fa-icon class="icon" [icon]="faTrash"></fa-icon>
      </button>
    </div>
  </div>

  <div fxLayout="column">
    <span class="sub-header" i18n="@@whenToSendEmailQuery">When should the email be sent?</span>
    <mat-checkbox
      class="taskClosedCheckbox"
      [checked]="action.trigger_options?.trigger_types?.task_closed ?? false"
      (change)="onTaskClosedTriggerChanged($event.checked)"
      i18n="@@taskIsFinished"
      color="primary"
      >Task is finished</mat-checkbox
    >
    <span fxLayout="row">
      <mat-checkbox
        class="incidentTriggerCheckbox"
        [checked]="areAllIncidentsSelected()"
        (change)="toggleAll($event.checked)"
        color="primary"
        [disabled]="incidentQuestions.length === 0"
        [indeterminate]="partiallyComplete()"
      >
        <span i18n="@@incidentIsReported">Incident is reported</span>
      </mat-checkbox>
      <div class="showIncidentsLink" *ngIf="incidentQuestions.length > 0">
        &nbsp;(
        <ng-container *ngIf="!showIncidents">
          <u i18n="@@show" (click)="showIncidents = true">show</u>
        </ng-container>
        <ng-container *ngIf="showIncidents">
          <u i18n="@@hide" (click)="showIncidents = false">hide</u>
        </ng-container>
        )
      </div>
    </span>
    <!-- incidentQuestions -->
    <div class="incidentQuestions" *ngIf="incidentQuestions.length > 0 && showIncidents">
      <div *ngFor="let question of incidentQuestions; index as i" class="incident-card">
        <mat-card>
          <mat-card-header>
            <mat-card-title-group>
              <mat-card-title>
                <span i18n="@@incident">Incident</span>&nbsp;{{ i + 1 }}
              </mat-card-title>
              <mat-card-subtitle>{{ question.questionText }}</mat-card-subtitle>
            </mat-card-title-group>
          </mat-card-header>
          <mat-card-content>
            <div *ngFor="let answer of question.answers | filterIncidents; index as j">
              <mat-checkbox
                [checked]="isIncidentSelected(answer)"
                (change)="updateIncidents($event.checked, answer)"
                color="primary"
              >
                {{ answer.answerText }}
              </mat-checkbox>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </div>

    <!-- no incidents note -->
    <div *ngIf="incidentQuestions.length === 0" class="incident-card">
      <mat-card class="no-incidents">
        <div class="no-incidents-content" fxLayout="column">
          <div class="hint-text">
            <span *ngIf="workflowId.length > 0" i18n="@@incidentsNotDefinedForWorkflowInDesigner"
              >You can't configure Incident Protocol recipients for this workflow, since no incident
              is defined for it. To change, please define an incident in the Workflow Designer</span
            >
            <span *ngIf="workflowId.length === 0" i18n="@@incidentsNotDefinedForWorkflowExternal"
              >You can't configure Incident Protocol recipients for this workflow, since no incident
              is defined for it. This workflow was created outside the application, therefore
              incidents must be defined through the same method</span
            >
          </div>
          <div fxLayout="row" fxLayoutAlign="end center" *ngIf="workflowId.length > 0">
            <oneapp-button
              class="goToWFDButton"
              i18n-text="@@goToDesigner"
              text="Go to Workflow Designer"
              (buttonClick)="onGoToDesigner()"
            ></oneapp-button>
          </div>
        </div>
      </mat-card>
    </div>
  </div>

  <div fxLayout="column" fxLayoutGap="8px">
    <span i18n="@@onlySendEmailWhenPartOfGroup" class="employeeGroupsDropdownText"
      >Only send notifications if the employee who is executing the task is part of the following
      groups:</span
    >
    <app-chips-autocomplete
      class="triggerGroupsDropdown"
      [title]="EMPLOYEE_GROUPS_TEXT"
      [inputPlaceholder]="EMPLOYEE_GROUP_AUTOCOMPLETE_PLACEHOLDER"
      [allElements]="convertedEmployeeGroups"
      (selectionchange)="onTriggerGroupsChanged($event)"
      [value]="action.trigger_options?.trigger_groups ?? []"
    ></app-chips-autocomplete>
  </div>

  <div fxLayout="column" fxLayoutGap="24px">
    <div fxLayout="column">
      <span class="sub-header" i18n="@@whoToReceiveEmailQuery">Who should receive the email?</span>
      <mat-checkbox
        class="recipientExecutorCheckbox"
        [checked]="action.recipient_options?.executors ?? false"
        (change)="onRecipientExecutorChanged($event.checked)"
        color="primary"
        i18n="@@employeeExecutingTheTaskCheckbox"
        >Employee executing the task</mat-checkbox
      >
      <mat-checkbox
        *ngIf="(action.trigger_options?.trigger_groups?.length ?? 0) == 0"
        class="recipientAllSupervisorsCheckbox"
        [checked]="action.recipient_options?.all_supervisors ?? false"
        (change)="onRecipientAllSupervisorsChanged($event.checked)"
        color="primary"
        i18n="@@supervisorsOfAllEmployeeGroupsCheckbox"
        >Supervisors of <b>all Employee groups</b>, of the employee executing the task</mat-checkbox
      >
      <mat-checkbox
        *ngIf="(action.trigger_options?.trigger_groups?.length ?? 0) > 0"
        class="recipientTriggerGroupSupervisorsCheckbox"
        [checked]="action.recipient_options?.trigger_group_supervisors ?? false"
        (change)="onRecipientTriggerGroupSupervisorsChanged($event.checked)"
        color="primary"
        i18n="@@supervisorsOfSelectedEmployeeGroupsCheckbox"
        >Supervisors of <b>selected "Employee groups"</b>, of the employee executing the
        task</mat-checkbox
      >
    </div>
    <app-chips-autocomplete
      class="additionalRecipientsDropdown"
      [title]="ADDITIONAL_RECIPIENTS_TEXT"
      [inputPlaceholder]="ADDITIONAL_RECIPIENTS_AUTOCOMPLETE_PLACEHOLDER"
      [allElements]="convertedEmployeesAndEmployeeGroups"
      (selectionchange)="onAdditionalRecipientsChanged($event)"
      [value]="action.recipient_options | toAdditionalRecipients"
    ></app-chips-autocomplete>
  </div>
</div>
